/*
 *  -----------------------------------------------------------------------
 *    <copyright file="step2-funnel-model.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import FormControl from '@mui/material/FormControl';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useTranslation } from 'hooks';
import { PanelBenchmark, PanelIntervalType, RowDataSourceTable } from 'types/graphql-global-types';
import { validateFields } from 'utils/validation';
import DataSourceSelector from 'views/components/central-monitoring/data-source-selector';
import { FieldSelector, Formats } from 'views/components/central-monitoring/field-selector';
import PanelConstants from 'views/components/central-monitoring/panels/add-panel/panel-constants';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';
import 'views/components/central-monitoring/panels/add-panel/styles.scss';

const Step2FunnelModel = forwardRef(
    ({ show, panelState, updateErrorState, setPage, page, updateState, canSave }: any, ref) => {
        const { t } = useTranslation();
        const initialState = {
            dataSource: panelState.dataSource?.dataSource,
            xAxisDataField: panelState.dataSource?.xAxisDataField,
            yAxisDataField: panelState.dataSource?.yAxisDataField,
            confidenceIntervalType: panelState.modelParams.confidenceIntervalType,
            modelParamsShow: panelState.modelParams.show,
            upperBigRed: panelState.modelParams.upperBigRed || 99.8,
            upperRed: panelState.modelParams.upperRed || 98.9,
            upperAmber: panelState.modelParams.upperAmber || 95.5,
            lowerAmber: panelState.modelParams.lowerAmber || 95.5,
            lowerRed: panelState.modelParams.lowerRed || 98.9,
            lowerBigRed: panelState.modelParams.lowerBigRed || 99.8,
            benchmarkType: panelState.modelParams.benchmarkType,
            benchmarkValue: panelState.modelParams.benchmarkValue,
            greenMaxScore: panelState.modelParams.greenMaxScore,
            amberMaxScore: panelState.modelParams.amberMaxScore,
        } as any;

        const initialErrors = {
            dataSource: '',
            upperBigRed: '',
            upperAmber: '',
            upperRed: '',
            lowerAmber: '',
            lowerRed: '',
            lowerBigRed: '',
            benchmarkValue: '',
            greenMaxScore: '',
            amberMaxScore: '',
            modelParamsShow: '',
        } as any;

        const [state, setState] = useState({ ...initialState });
        const [errors, setErrors] = useState({ ...initialErrors });
        const { modelParamsShow } = PanelConstants;

        const getShowParam = (panel: any): string => {
            if (panel.modelParams.hideUpper) return 'hide-upper';

            if (panel.modelParams.hideLower) return 'hide-lower';

            return 'all';
        };

        useEffect(() => {
            if (panelState)
                setState({
                    dataSource: panelState.dataSource?.dataSource,
                    xAxisDataField: panelState.dataSource?.xAxisDataField,
                    yAxisDataField: panelState.dataSource?.yAxisDataField,
                    confidenceIntervalType: panelState.modelParams.confidenceIntervalType,
                    modelParamsShow: getShowParam(panelState),
                    upperBigRed: panelState.modelParams.upperBigRed,
                    upperRed: panelState.modelParams.upperRed,
                    upperAmber: panelState.modelParams.upperAmber,
                    lowerAmber: panelState.modelParams.lowerAmber,
                    lowerRed: panelState.modelParams.lowerRed,
                    lowerBigRed: panelState.modelParams.lowerBigRed,
                    benchmarkType: panelState.modelParams.benchmarkType,
                    benchmarkValue: panelState.modelParams.benchmarkValue,
                    greenMaxScore: panelState.modelParams.greenMaxScore,
                    amberMaxScore: panelState.modelParams.amberMaxScore,
                });
        }, [panelState]);

        const checkThresholdValidation = (
            bigRed: number,
            red: number,
            amber: number,
            prefix: string,
            required?: boolean,
        ) => {
            const capitalizeFirstLetter = (string: string) => string.charAt(0).toUpperCase() + string.slice(1);
            const error = {} as any;
            if ((bigRed > 99.9 || bigRed < 80) && required)
                error[`${prefix}BigRed`] = t('admin:numericValueMinMax', { min: '80.0', max: '99.9' });
            else if (bigRed <= red)
                error[`${prefix}BigRed`] = t('admin:BigRedThresholdShouldBeBiggerRedThreshold', {
                    prefix: capitalizeFirstLetter(prefix),
                });
            else error[`${prefix}BigRed`] = '';

            if ((red > 99.9 || red < 80) && required)
                error[`${prefix}Red`] = t('admin:numericValueMinMax', { min: '80.0', max: '99.9' });
            else if (red >= bigRed)
                error[`${prefix}Red`] = t('admin:RedThresholdShouldBeLessThenBigRedThreshold', {
                    prefix: capitalizeFirstLetter(prefix),
                });
            else if (red <= amber)
                error[`${prefix}Red`] = t('admin:RedThresholdShouldBeBiggerThenAmberThreshold', {
                    prefix: capitalizeFirstLetter(prefix),
                });
            else error[`${prefix}Red`] = '';

            if ((amber > 99.9 || amber < 80) && required)
                error[`${prefix}Amber`] = t('admin:numericValueMinMax', { min: '80.0', max: '99.9' });
            else if (amber >= red)
                error[`${prefix}Amber`] = t('admin:AmberThresholdShouldBeLessThenRedThreshold', {
                    prefix: capitalizeFirstLetter(prefix),
                });
            else error[`${prefix}Amber`] = '';
            return error;
        };

        const checkRequiredFields = (fields: string[]) => {
            let isValid = true;
            let newErrors = { ...errors };
            fields.forEach((field) => {
                let value = (state as any)[field];
                if (typeof value === 'number') value = `${value}`;

                let error = {};
                if (field === 'benchmarkValue') {
                    if (value >= 1 || value <= 0) {
                        error = { benchmarkValue: t('admin:numericValueHasToBeBetween', { min: '0', max: '1' }) };
                    }
                } else if (field === 'checkThreshold') {
                    error = {
                        ...checkThresholdValidation(state.upperBigRed, state.upperRed, state.upperAmber, 'upper', true),
                        ...checkThresholdValidation(state.lowerBigRed, state.lowerRed, state.lowerAmber, 'lower', true),
                    };
                } else {
                    error = validateFields(value, field, 0, true);
                }
                newErrors = {
                    ...newErrors,
                    ...error,
                };
            });
            if (Object.keys(newErrors).filter((item) => (newErrors as any)[item]).length > 0) isValid = false;

            setErrors({ ...newErrors });
            return isValid;
        };

        const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
            const error = validateFields(value, field, length, required);
            setErrors({
                ...errors,
                ...error,
            });
        };

        const numberValidation = (
            value: number,
            fieldName: string,
            min: number,
            max: number,
            errorText: string,
            equal: boolean,
        ) => {
            let error = { [fieldName]: '' };
            if ((equal && (value >= max || value <= min)) || value > max || value < min)
                error = { [fieldName]: errorText };
            else if (['upperBigRed', 'upperAmber', 'upperRed'].includes(fieldName))
                error = checkThresholdValidation(
                    fieldName === 'upperBigRed' ? value : state.upperBigRed,
                    fieldName === 'upperRed' ? value : state.upperRed,
                    fieldName === 'upperAmber' ? value : state.upperAmber,
                    'upper',
                );
            else if (['lowerBigRed', 'lowerAmber', 'lowerRed'].includes(fieldName))
                error = checkThresholdValidation(
                    fieldName === 'lowerBigRed' ? value : state.lowerBigRed,
                    fieldName === 'lowerRed' ? value : state.lowerRed,
                    fieldName === 'lowerAmber' ? value : state.lowerAmber,
                    'lower',
                );
            setErrors({
                ...errors,
                ...error,
            });

            return !!error[fieldName] || !!errors[fieldName];
        };

        const onChangeNumber =
            (fieldName: string, errorText?: string, min?: number, max?: number, key?: string, equal?: boolean) =>
            (event: any) => {
                const { value } = event.target;
                let numberValidationError = null;
                let error = { [fieldName]: '' };
                updateState({ target: { name: key || fieldName, value: +value } });

                setState({
                    ...state,
                    [fieldName]: +value,
                });
                if (min !== undefined || max !== undefined)
                    numberValidationError = numberValidation(+value, fieldName, min, max, errorText, equal);

                if (numberValidationError) return;

                if (fieldName === 'amberMaxScore' && value <= state.greenMaxScore)
                    error = { [fieldName]: t('admin:amberMaxScoreShouldBeBiggerThenGreen') };
                if (fieldName === 'amberMaxScore' && value >= state.greenMaxScore)
                    error = {
                        ...error,
                        greenMaxScore: null,
                    };
                if (fieldName === 'greenMaxScore' && value >= state.amberMaxScore)
                    error = { [fieldName]: t('admin:amberMaxScoreShouldBeBiggerThenGreen') };
                if (fieldName === 'greenMaxScore' && value <= state.amberMaxScore)
                    error = {
                        ...error,
                        amberMaxScore: null,
                    };
                setErrors({
                    ...errors,
                    ...error,
                });
            };

        const onChange = (fieldName: string, length?: number, required?: boolean, key?: string, value?: string) => {
            if (fieldName === 'dataSource') {
                setState({ ...state, dataSource: value, xAxisDataField: '', yAxisDataField: '' });
                updateState({ target: { name: 'dataSource.dataSource', value } });
                updateState({ target: { name: 'dataSource.xAxisDataField', value: '' } });
                updateState({ target: { name: 'dataSource.yAxisDataField', value: '' } });
            } else {
                setState({ ...state, [fieldName]: value });
                updateState({ target: { name: key || fieldName, value } });
            }
            validate(value, fieldName, length, required);
        };

        const getIntervalTypeOptions = () => {
            const options = Object.values(PanelIntervalType).map((field: string) => ({
                id: field,
                text: t(`admin:${field}`),
                active: true,
                default: false,
            }));
            options.unshift({ id: '', text: t('admin:selectConfidenceIntervalTypeR'), active: true, default: true });
            return options;
        };

        const intervalTypeOptions = useMemo(getIntervalTypeOptions, []);

        const getModelParamsShowOptions = () =>
            Object.values(modelParamsShow).map((field: string) => ({
                id: field,
                text: t(`admin:${field}`),
                active: true,
                default: false,
            }));

        const modelParamsShowOptions = useMemo(getModelParamsShowOptions, []);

        const getBenchmarkTypeOptions = () => {
            const options = Object.values(PanelBenchmark).map((field: string) => ({
                id: field,
                text: t(`admin:${field}`),
                active: true,
                default: false,
            }));
            options.unshift({ id: '', text: t('admin:selectBenchmarkTypeR'), active: true, default: true });
            return options;
        };

        const benchmarkTypeOptions = useMemo(getBenchmarkTypeOptions, []);

        const validationOnUpdate = () => {
            const requiredFields = [
                'dataSource',
                'xAxisDataField',
                'yAxisDataField',
                'confidenceIntervalType',
                'modelParamsShow',
                'checkThreshold',
                'benchmarkType',
                'amberMaxScore',
                'greenMaxScore',
            ];
            if (state.benchmarkType === PanelBenchmark.STUDY_SPECIFIC) requiredFields.push('benchmarkValue');

            return checkRequiredFields(requiredFields);
        };

        const onNext = () => {
            if (validationOnUpdate()) {
                setPage({ ...page, number: 3, available: 4 });
            } else if (page.number !== 2) setPage({ ...page, number: 2, available: page.available });
        };

        const cleanStep = () => {
            setState({ ...initialState });
            setErrors({ ...initialErrors });
        };

        useEffect(() => {
            updateErrorState(errors, 2);
        }, [errors]);

        useImperativeHandle(ref, () => ({
            nextButton() {
                onNext();
            },
            cleanStep() {
                cleanStep();
            },
            validationOnUpdate() {
                return validationOnUpdate();
            },
        }));

        return (
            <div style={!show ? { display: 'none' } : {}}>
                <div className="type-edc-wrapper mt">
                    <FormControl variant="outlined" className="form-control-type mr select-with-error">
                        <DataSourceSelector
                            label={t('admin:rowData')}
                            onChange={(value: string) => {
                                onChange('dataSource', null, false, 'dataSource.dataSource', value);
                            }}
                            fieldSelect="dataSource"
                            value={state.dataSource}
                            labelField="rowDataSource"
                            showDefault={false}
                            tables={[RowDataSourceTable.SITE]}
                            error={errors.dataSource}
                            customClassName="add-panel-rowDataSource"
                            tag="PANEL"
                            disabled={!canSave}
                            appendPopupToSelect
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type select-with-error">
                        <FieldSelector
                            label={t('admin:xAxisDataR')}
                            dataSource={state.dataSource}
                            formats={[Formats.Int, Formats.Float]}
                            value={state.xAxisDataField}
                            onChange={(value: string) => {
                                onChange('xAxisDataField', null, false, 'dataSource.xAxisDataField', value);
                            }}
                            labelField="xAxisDataField"
                            fieldSelect="xAxisDataField"
                            customId="add-panel-xAxisDataField"
                            error={errors.xAxisDataField}
                            excludeValue={(field: any) => field.key !== state.yAxisDataField}
                            searchable
                            disabled={!canSave}
                            appendPopupToSelect
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper">
                    <FormControl variant="outlined" className="form-control-type mr select-with-error">
                        <FieldSelector
                            label={t('admin:yAxisData')}
                            dataSource={state.dataSource}
                            formats={[Formats.Int, Formats.Float]}
                            value={state.yAxisDataField}
                            onChange={(value: string) => {
                                onChange('yAxisDataField', null, false, 'dataSource.yAxisDataField', value);
                            }}
                            labelField="yAxisDataField"
                            fieldSelect="yAxisDataField"
                            customId="add-panel-yAxisDataField"
                            error={errors.yAxisDataField}
                            excludeValue={(field: any) => field.key !== state.xAxisDataField}
                            searchable
                            disabled={!canSave}
                            appendPopupToSelect
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type select-with-error">
                        <SelectWithCategories
                            customClassName="add-panel-confidenceIntervalType"
                            showWarning={false}
                            fieldSelect="confidenceIntervalType"
                            onChange={(fieldName?: string) => (event: any) => {
                                onChange(
                                    fieldName,
                                    null,
                                    false,
                                    'modelParams.confidenceIntervalType',
                                    event.target?.value,
                                );
                            }}
                            label={t('admin:confidenceIntervalTypeR')}
                            filterable={false}
                            labelField="confidenceIntervalType"
                            dataArray={intervalTypeOptions}
                            customId="add-panel-confidenceIntervalType"
                            currentState={state.confidenceIntervalType}
                            error={errors.confidenceIntervalType}
                            appendPopupToSelect
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper mt">
                    <FormControl variant="outlined" className="form-control-type mr select-with-error">
                        <SelectWithCategories
                            customClassName="add-panel-modelParamsShow"
                            showWarning={false}
                            fieldSelect="modelParamsShow"
                            onChange={(fieldName?: string) => (event: any) => {
                                const value = event.target?.value;
                                if (value.match('hide-lower')) {
                                    updateState({ target: { name: 'modelParams.hideLower', value: true } });
                                    updateState({ target: { name: 'modelParams.hideUpper', value: false } });
                                } else if (value.match('hide-upper')) {
                                    updateState({ target: { name: 'modelParams.hideLower', value: false } });
                                    updateState({ target: { name: 'modelParams.hideUpper', value: true } });
                                } else {
                                    updateState({ target: { name: 'modelParams.hideLower', value: false } });
                                    updateState({ target: { name: 'modelParams.hideUpper', value: false } });
                                }
                                onChange(fieldName, null, false, 'modelParams.show', event.target?.value);
                            }}
                            label={t('admin:showHideThresholdsR')}
                            filterable={false}
                            labelField="modelParamsShow"
                            dataArray={modelParamsShowOptions}
                            customId="add-panel-modelParamsShow"
                            currentState={state.modelParamsShow}
                            error={errors.modelParamsShow}
                            appendPopupToSelect
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type">
                        <TextField
                            label={t('admin:upperBigRedThresholdR')}
                            id="add-panel-upperBigRed"
                            fullWidth
                            type="number"
                            value={state.upperBigRed}
                            onChange={onChangeNumber(
                                'upperBigRed',
                                t('admin:numericValueMinMax', { min: '80.0', max: '99.9' }),
                                80,
                                99.9,
                                'modelParams.upperBigRed',
                            )}
                            placeholder={t('admin:enterUpperBigRedThresholdR')}
                            helperText={
                                <span style={errors.upperBigRed ? { color: '#ae0015' } : {}} className="remaining-text">
                                    {errors.upperBigRed
                                        ? errors.upperBigRed
                                        : t('admin:numericValueMinMax', { min: '80.0', max: '99.9' })}
                                </span>
                            }
                            variant="outlined"
                            color="secondary"
                            className="before-select"
                            error={!!errors.upperBigRed}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper mt">
                    <FormControl variant="outlined" className="form-control-type mr">
                        <TextField
                            label={t('admin:upperRedThresholdR')}
                            id="add-panel-upperRed"
                            fullWidth
                            type="number"
                            value={state.upperRed}
                            onChange={onChangeNumber(
                                'upperRed',
                                t('admin:numericValueMinMax', { min: '80.0', max: '99.9' }),
                                80,
                                99.9,
                                'modelParams.upperRed',
                            )}
                            placeholder={t('admin:enterUpperRedThresholdR')}
                            helperText={
                                <span style={errors.upperRed ? { color: '#ae0015' } : {}} className="remaining-text">
                                    {errors.upperRed
                                        ? errors.upperRed
                                        : t('admin:numericValueMinMax', { min: '80.0', max: '99.9' })}
                                </span>
                            }
                            variant="outlined"
                            color="secondary"
                            className="before-select"
                            error={!!errors.upperRed}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type">
                        <TextField
                            label={t('admin:upperAmberThresholdR')}
                            id="add-panel-upperAmber"
                            fullWidth
                            type="number"
                            value={state.upperAmber}
                            onChange={onChangeNumber(
                                'upperAmber',
                                t('admin:numericValueMinMax', { min: '80.0', max: '99.9' }),
                                80,
                                99.9,
                                'modelParams.upperAmber',
                            )}
                            placeholder={t('admin:enterUpperAmberThresholdR')}
                            helperText={
                                <span style={errors.upperAmber ? { color: '#ae0015' } : {}} className="remaining-text">
                                    {errors.upperAmber
                                        ? errors.upperAmber
                                        : t('admin:numericValueMinMax', { min: '80.0', max: '99.9' })}
                                </span>
                            }
                            variant="outlined"
                            color="secondary"
                            className="before-select"
                            error={!!errors.upperAmber}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper mt">
                    <FormControl variant="outlined" className="form-control-type mr">
                        <TextField
                            label={t('admin:lowerAmberThresholdR')}
                            id="add-panel-lowerAmber"
                            fullWidth
                            type="number"
                            value={state.lowerAmber}
                            onChange={onChangeNumber(
                                'lowerAmber',
                                t('admin:numericValueMinMax', { min: '80.0', max: '99.9' }),
                                80,
                                99.9,
                                'modelParams.lowerAmber',
                            )}
                            placeholder={t('admin:enterLowerAmberThresholdR')}
                            helperText={
                                <span style={errors.lowerAmber ? { color: '#ae0015' } : {}} className="remaining-text">
                                    {errors.lowerAmber
                                        ? errors.lowerAmber
                                        : t('admin:numericValueMinMax', { min: '80.0', max: '99.9' })}
                                </span>
                            }
                            variant="outlined"
                            color="secondary"
                            className="before-select"
                            error={!!errors.lowerAmber}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type">
                        <TextField
                            label={t('admin:lowerRedThresholdR')}
                            id="add-panel-lowerRed"
                            fullWidth
                            type="number"
                            value={state.lowerRed}
                            onChange={onChangeNumber(
                                'lowerRed',
                                t('admin:numericValueMinMax', { min: '80.0', max: '99.9' }),
                                80,
                                99.9,
                                'modelParams.lowerRed',
                            )}
                            placeholder={t('admin:enterLowerRedThresholdR')}
                            helperText={
                                <span style={errors.lowerRed ? { color: '#ae0015' } : {}} className="remaining-text">
                                    {errors.lowerRed
                                        ? errors.lowerRed
                                        : t('admin:numericValueMinMax', { min: '80.0', max: '99.9' })}
                                </span>
                            }
                            variant="outlined"
                            color="secondary"
                            className="before-select"
                            error={!!errors.lowerRed}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper">
                    <FormControl variant="outlined" className="form-control-type not-full">
                        <TextField
                            label={t('admin:lowerBigRedThresholdR')}
                            id="add-panel-lowerBigRed"
                            fullWidth
                            type="number"
                            value={state.lowerBigRed}
                            onChange={onChangeNumber(
                                'lowerBigRed',
                                t('admin:numericValueMinMax', { min: '80.0', max: '99.9' }),
                                80,
                                99.9,
                                'modelParams.lowerBigRed',
                            )}
                            placeholder={t('admin:enterLowerBigRedThresholdR')}
                            helperText={
                                <span style={errors.lowerBigRed ? { color: '#ae0015' } : {}} className="remaining-text">
                                    {errors.lowerBigRed
                                        ? errors.lowerBigRed
                                        : t('admin:numericValueMinMax', { min: '80.0', max: '99.9' })}
                                </span>
                            }
                            variant="outlined"
                            color="secondary"
                            className="before-select"
                            error={!!errors.lowerBigRed}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper mt">
                    <FormControl variant="outlined" className="form-control-type not-full mr select-with-error">
                        <SelectWithCategories
                            customClassName="add-panel-benchmarkType"
                            showWarning={false}
                            fieldSelect="benchmarkType"
                            label={t('admin:benchmarkTypeR')}
                            filterable={false}
                            labelField="benchmarkType"
                            onChange={(fieldName?: string) => (event: any) => {
                                onChange(fieldName, null, false, 'modelParams.benchmarkType', event.target?.value);
                            }}
                            dataArray={benchmarkTypeOptions}
                            customId="add-panel-benchmarkType"
                            currentState={state.benchmarkType}
                            error={errors.benchmarkType}
                        />
                    </FormControl>
                    {state.benchmarkType === PanelBenchmark.STUDY_SPECIFIC && (
                        <FormControl variant="outlined" className="form-control-type not-full">
                            <TextField
                                label={t('admin:benchmarkR')}
                                id="add-panel-benchmarkValue"
                                fullWidth
                                type="number"
                                value={state.benchmarkValue}
                                onChange={onChangeNumber(
                                    'benchmarkValue',
                                    t('admin:numericValueHasToBeBetween', { min: '0', max: '1' }),
                                    0,
                                    1,
                                    'modelParams.benchmarkValue',
                                    true,
                                )}
                                placeholder={t('admin:enterBenchmarkR')}
                                helperText={
                                    <span
                                        style={errors.benchmarkValue ? { color: '#ae0015' } : {}}
                                        className="remaining-text"
                                    >
                                        {errors.benchmarkValue
                                            ? errors.benchmarkValue
                                            : t('admin:numericValueHasToBeBetween', { min: '0', max: '1' })}
                                    </span>
                                }
                                variant="outlined"
                                color="secondary"
                                className="before-select"
                            />
                        </FormControl>
                    )}
                </div>
                <div className="type-edc-wrapper mt">
                    <FormControl variant="outlined" className="form-control-type mr">
                        <TextField
                            label={t('admin:greenMaxScoreR')}
                            id="add-panel-greenMaxScore"
                            fullWidth
                            type="number"
                            value={state.greenMaxScore}
                            onChange={onChangeNumber(
                                'greenMaxScore',
                                t('admin:numericValueMinMax', { min: '1', max: '100' }),
                                1,
                                100,
                                'modelParams.greenMaxScore',
                            )}
                            placeholder={t('admin:enterGreenMaxScoreR')}
                            helperText={
                                <span
                                    style={errors.greenMaxScore ? { color: '#ae0015' } : {}}
                                    className="remaining-text"
                                >
                                    {errors.greenMaxScore
                                        ? errors.greenMaxScore
                                        : t('admin:numericValueMinMax', { min: '1', max: '100' })}
                                </span>
                            }
                            variant="outlined"
                            color="secondary"
                            className="before-select"
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type">
                        <TextField
                            label={t('admin:amberMaxScore')}
                            id="add-panel-amberMaxScore"
                            fullWidth
                            type="number"
                            value={state.amberMaxScore}
                            onChange={onChangeNumber(
                                'amberMaxScore',
                                t('admin:numericValueMinMax', { min: '1', max: '100' }),
                                1,
                                100,
                                'modelParams.amberMaxScore',
                            )}
                            placeholder={t('admin:enterAmberMaxScoreR')}
                            helperText={
                                <span
                                    style={errors.amberMaxScore ? { color: '#ae0015' } : {}}
                                    className="remaining-text"
                                >
                                    {errors.amberMaxScore
                                        ? errors.amberMaxScore
                                        : t('admin:numericValueMinMax', { min: '1', max: '100' })}
                                </span>
                            }
                            variant="outlined"
                            color="secondary"
                            className="before-select"
                        />
                    </FormControl>
                </div>
            </div>
        );
    },
);

export default Step2FunnelModel;
